import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  cancelOrderThunk,
  clearCurrent,
  completeOrderThunk,
  createCalibrationThunk,
  createOrderThunk,
  createPlanThunk,
  createRecordThunk,
  loadAlertsPreview,
  loadCalibrations,
  loadOneEquipment,
  loadOrders,
  loadPlans,
  loadRecords,
  updatePlanThunk,
} from '../features/maintenance/maintenanceSlice';
import type {
  CalibrationRecord,
  CalibrationRecordPayload,
  CalibrationResult,
  CalibrationStatus,
  MaintenanceAlertPreviewItem,
  MaintenanceOrder,
  MaintenanceOrderPayload,
  MaintenanceOrderStatus,
  MaintenanceOrderType,
  MaintenancePlanFrequency,
  MaintenancePlanPayload,
  MaintenanceRecordPayload,
} from '../features/maintenance/types';
import { AppDispatch, RootState } from '../store/store';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { DateCell } from '../components/DateCell';
import { AttachmentsList } from '../components/AttachmentsList';

const FREQ_LABEL: Record<MaintenancePlanFrequency, string> = {
  diaria: 'Diaria',
  semanal: 'Semanal',
  mensual: 'Mensual',
  trimestral: 'Trimestral',
  semestral: 'Semestral',
  anual: 'Anual',
  por_horas: 'Por horas',
};

const ORDER_TYPE_LABEL: Record<MaintenanceOrderType, string> = {
  preventivo: 'Preventivo',
  correctivo: 'Correctivo',
  calibracion: 'Calibración',
  inspeccion: 'Inspección',
};

const ORDER_STATUS_CLASS: Record<MaintenanceOrderStatus, string> = {
  programada: 'bg-blue-100 text-blue-800',
  en_curso: 'bg-amber-100 text-amber-800',
  completada: 'bg-green-100 text-green-800',
  cancelada: 'bg-zinc-200 text-zinc-700',
};

const CALIBRATION_STATUS_CLASS: Record<CalibrationStatus, string> = {
  vigente: 'bg-green-100 text-green-800',
  por_vencer: 'bg-amber-100 text-amber-800',
  vencido: 'bg-red-100 text-red-800',
  fuera_de_uso: 'bg-zinc-200 text-zinc-700',
};

const CALIBRATION_STATUS_LABEL: Record<CalibrationStatus, string> = {
  vigente: 'Vigente',
  por_vencer: 'Por vencer',
  vencido: 'Vencido',
  fuera_de_uso: 'Fuera de uso',
};

const CALIBRATION_RESULT_LABEL: Record<CalibrationResult, string> = {
  conforme: 'Conforme',
  no_conforme: 'No conforme',
};

export function EquipmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const { canEdit } = usePlatformAccessLevel('maintenance.access');
  const { current, plans, orders, records, calibrations } = useSelector((s: RootState) => s.maintenance);
  const [tab, setTab] = useState<'planes' | 'ordenes' | 'registros' | 'calibraciones' | 'documentacion'>('planes');

  useEffect(() => {
    if (!id) return;
    dispatch(loadOneEquipment(id));
    dispatch(loadPlans(id));
    dispatch(loadOrders(id));
    dispatch(loadRecords(undefined));
    return () => {
      dispatch(clearCurrent());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (!id || !current?.isInstrument) return;
    dispatch(loadCalibrations(id));
  }, [dispatch, id, current?.isInstrument]);

  if (!current) {
    return <p className="text-muted-foreground">Cargando…</p>;
  }

  return (
    <div className="space-y-4">
      <div>
        <Link to="/mantenimiento/equipos" className="text-sm text-primary hover:underline">
          ← Volver a equipos
        </Link>
      </div>
      <header className="border rounded-md p-4 bg-card">
        <div className="flex justify-between items-start gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold">{current.name}</h1>
            <p className="text-sm text-muted-foreground font-mono">{current.code}</p>
          </div>
          <div className="text-sm space-y-1">
            <div>
              <span className="text-muted-foreground">Estado: </span>
              <span className="capitalize">{current.status.replace(/_/g, ' ')}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Criticidad: </span>
              <span className="capitalize">{current.criticality}</span>
            </div>
            {current.isInstrument && (
              <>
                <div>
                  <span className="text-muted-foreground">Estado metrológico: </span>
                  {current.calibrationStatus ? (
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-xs ${CALIBRATION_STATUS_CLASS[current.calibrationStatus]}`}
                    >
                      {CALIBRATION_STATUS_LABEL[current.calibrationStatus]}
                    </span>
                  ) : (
                    '—'
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground">Próx. calibración: </span>
                  {current.nextCalibrationAt ? <DateCell value={current.nextCalibrationAt} /> : '—'}
                </div>
                {current.calibrationStandard && (
                  <div>
                    <span className="text-muted-foreground">Patrón: </span>
                    {current.calibrationStandard}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </header>

      <div className="border-b flex gap-1">
        <TabButton active={tab === 'planes'} onClick={() => setTab('planes')}>
          Planes ({plans.length})
        </TabButton>
        <TabButton active={tab === 'ordenes'} onClick={() => setTab('ordenes')}>
          Órdenes ({orders.length})
        </TabButton>
        <TabButton active={tab === 'registros'} onClick={() => setTab('registros')}>
          Registros
        </TabButton>
        {current.isInstrument && (
          <TabButton active={tab === 'calibraciones'} onClick={() => setTab('calibraciones')}>
            Calibraciones ({calibrations.length})
          </TabButton>
        )}
        <TabButton active={tab === 'documentacion'} onClick={() => setTab('documentacion')}>
          Documentación
        </TabButton>
      </div>

      {tab === 'planes' && id && <PlansTab equipmentId={id} canEdit={canEdit} />}
      {tab === 'ordenes' && id && <OrdersTab equipmentId={id} canEdit={canEdit} />}
      {tab === 'registros' && <RecordsTab orders={orders} records={records} canEdit={canEdit} />}
      {tab === 'calibraciones' && id && current.isInstrument && (
        <CalibrationsTab equipmentId={id} records={calibrations} canEdit={canEdit} />
      )}
      {tab === 'documentacion' && id && (
        <div className="border rounded-md p-4 bg-card">
          <AttachmentsList
            resourceType="equipment"
            resourceId={id}
            readOnly={!canEdit}
            title="Documentación del equipo"
            accept=".pdf,.doc,.docx,.zip,image/*"
          />
        </div>
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 text-sm border-b-2 ${
        active ? 'border-primary text-primary' : 'border-transparent text-muted-foreground'
      }`}
    >
      {children}
    </button>
  );
}

const numberOrUndefined = (value: unknown): number | undefined => {
  if (value === '' || value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const planSchema = z
  .object({
    name: z.string().min(1).max(255),
    frequency: z.enum(['diaria', 'semanal', 'mensual', 'trimestral', 'semestral', 'anual', 'por_horas']),
    intervalDays: z.coerce.number().int().positive().optional(),
    startsOn: z.string().optional(),
    alertEnabled: z.boolean().optional(),
    alertDaysBefore: z.number().int().min(1).max(365).optional(),
  })
  .refine((v) => !v.alertEnabled || v.alertDaysBefore !== undefined, {
    message: 'Indicá los días de anticipación (1–365)',
    path: ['alertDaysBefore'],
  });
type PlanForm = z.infer<typeof planSchema>;

const PLAN_FORM_DEFAULTS: PlanForm = {
  name: '',
  frequency: 'mensual',
  startsOn: '',
  alertEnabled: false,
  alertDaysBefore: undefined,
};

function PlanDueIndicator({ item }: { item?: MaintenanceAlertPreviewItem }) {
  if (!item) {
    return <span className="text-muted-foreground">—</span>;
  }
  const dueDate = new Date(item.nextDueAt).toLocaleDateString('es-AR');
  if (item.state === 'overdue') {
    return (
      <span className="text-red-700 font-medium">
        {dueDate} · VENCIDO ({Math.abs(item.daysUntilDue)} día/s)
      </span>
    );
  }
  return (
    <span className="text-amber-700 font-medium">
      {dueDate} · vence en {item.daysUntilDue} día/s
    </span>
  );
}

function PlansTab({ equipmentId, canEdit }: { equipmentId: string; canEdit: boolean }) {
  const dispatch = useDispatch<AppDispatch>();
  const { plans, alertsPreview } = useSelector((s: RootState) => s.maintenance);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<PlanForm>({
    resolver: zodResolver(planSchema),
    defaultValues: PLAN_FORM_DEFAULTS,
  });

  useEffect(() => {
    dispatch(loadAlertsPreview());
  }, [dispatch]);

  const alertEnabled = watch('alertEnabled');
  const previewByPlanId = new Map((alertsPreview?.items ?? []).map((item) => [item.planId, item]));

  const closeForm = () => {
    reset(PLAN_FORM_DEFAULTS);
    setEditingId(null);
    setOpen(false);
  };

  const startCreate = () => {
    reset(PLAN_FORM_DEFAULTS);
    setEditingId(null);
    setOpen(true);
  };

  const startEdit = (plan: (typeof plans)[number]) => {
    reset({
      name: plan.name,
      frequency: plan.frequency,
      intervalDays: plan.intervalDays ?? undefined,
      startsOn: plan.startsOn ? plan.startsOn.slice(0, 10) : '',
      alertEnabled: plan.alertEnabled,
      alertDaysBefore: plan.alertDaysBefore ?? undefined,
    });
    setEditingId(plan.id);
    setOpen(true);
  };

  const submit = handleSubmit(async (values) => {
    const payload: MaintenancePlanPayload = {
      name: values.name,
      frequency: values.frequency,
      intervalDays: values.intervalDays ?? null,
      startsOn: values.startsOn && values.startsOn.length > 0 ? values.startsOn : null,
      alertEnabled: values.alertEnabled ?? false,
      alertDaysBefore: values.alertEnabled ? values.alertDaysBefore ?? null : null,
    };
    if (editingId) {
      await dispatch(updatePlanThunk({ equipmentId, planId: editingId, payload })).unwrap();
    } else {
      await dispatch(createPlanThunk({ equipmentId, payload })).unwrap();
    }
    await dispatch(loadAlertsPreview());
    closeForm();
  });

  return (
    <div className="space-y-3">
      {canEdit && (
        <button
          onClick={() => (open ? closeForm() : startCreate())}
          className="rounded-md px-3 py-1.5 text-sm bg-primary text-primary-foreground hover:bg-primary/90"
        >
          {open ? 'Cancelar' : '+ Nuevo plan'}
        </button>
      )}
      {open && (
        <form onSubmit={submit} className="border rounded-md p-3 grid grid-cols-1 md:grid-cols-4 gap-3 bg-muted/30">
          <FieldInline label="Nombre" error={errors.name?.message}>
            <input className="border rounded-md px-2 py-1.5 w-full" {...register('name')} />
          </FieldInline>
          <FieldInline label="Frecuencia">
            <select className="border rounded-md px-2 py-1.5 w-full" {...register('frequency')}>
              {Object.entries(FREQ_LABEL).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </FieldInline>
          <FieldInline label="Intervalo (días, opcional)">
            <input
              type="number"
              min={1}
              className="border rounded-md px-2 py-1.5 w-full"
              {...register('intervalDays')}
            />
          </FieldInline>
          <FieldInline label="Comienza el">
            <input type="date" className="border rounded-md px-2 py-1.5 w-full" {...register('startsOn')} />
          </FieldInline>
          <FieldInline label="Alarma de vencimiento">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" {...register('alertEnabled')} />
              Notificar mantenimiento próximo / vencido
            </label>
          </FieldInline>
          <FieldInline label="Días de anticipación" error={errors.alertDaysBefore?.message}>
            <input
              type="number"
              min={1}
              max={365}
              disabled={!alertEnabled}
              className="border rounded-md px-2 py-1.5 w-full disabled:opacity-50"
              {...register('alertDaysBefore', { setValueAs: numberOrUndefined })}
            />
          </FieldInline>
          <div className="md:col-span-4 flex justify-end">
            <button
              type="submit"
              className="rounded-md px-3 py-1.5 text-sm bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {editingId ? 'Guardar cambios' : 'Crear plan'}
            </button>
          </div>
        </form>
      )}
      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <Th>Nombre</Th>
              <Th>Frecuencia</Th>
              <Th>Intervalo (días)</Th>
              <Th>Activo</Th>
              <Th>Comienza el</Th>
              <Th>Alarma</Th>
              <Th>Próximo vencimiento</Th>
              {canEdit && <Th>Acciones</Th>}
            </tr>
          </thead>
          <tbody>
            {plans.length === 0 && (
              <tr>
                <td colSpan={canEdit ? 8 : 7} className="px-3 py-4 text-center text-muted-foreground">
                  Sin planes
                </td>
              </tr>
            )}
            {plans.map((p) => (
              <tr key={p.id} className="border-t">
                <td className="px-3 py-2">{p.name}</td>
                <td className="px-3 py-2">{FREQ_LABEL[p.frequency]}</td>
                <td className="px-3 py-2">{p.intervalDays ?? '—'}</td>
                <td className="px-3 py-2">{p.active ? 'Sí' : 'No'}</td>
                <td className="px-3 py-2">{p.startsOn ? <DateCell value={p.startsOn} /> : '—'}</td>
                <td className="px-3 py-2">
                  {p.alertEnabled ? `Sí (${p.alertDaysBefore ?? '—'} días antes)` : 'No'}
                </td>
                <td className="px-3 py-2">
                  <PlanDueIndicator item={previewByPlanId.get(p.id)} />
                </td>
                {canEdit && (
                  <td className="px-3 py-2">
                    <button
                      onClick={() => startEdit(p)}
                      className="text-primary hover:underline text-sm"
                    >
                      Editar
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const orderSchema = z.object({
  type: z.enum(['preventivo', 'correctivo', 'calibracion', 'inspeccion']),
  scheduledAt: z.string().min(1, 'Requerido'),
  description: z.string().trim().max(2000).optional(),
});
type OrderForm = z.infer<typeof orderSchema>;

function OrdersTab({ equipmentId, canEdit }: { equipmentId: string; canEdit: boolean }) {
  const dispatch = useDispatch<AppDispatch>();
  const { orders } = useSelector((s: RootState) => s.maintenance);
  const [open, setOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<OrderForm>({
    resolver: zodResolver(orderSchema),
    defaultValues: { type: 'preventivo', scheduledAt: '', description: '' },
  });

  const submit = handleSubmit(async (values) => {
    const payload: MaintenanceOrderPayload = {
      equipmentId,
      type: values.type,
      scheduledAt: new Date(values.scheduledAt).toISOString(),
      description: values.description && values.description.length > 0 ? values.description : null,
    };
    await dispatch(createOrderThunk(payload)).unwrap();
    reset({ type: 'preventivo', scheduledAt: '', description: '' });
    setOpen(false);
  });

  const handleComplete = useCallback(
    async (o: MaintenanceOrder) => {
      const notes = prompt('Observaciones (opcional):') ?? null;
      await dispatch(completeOrderThunk({ id: o.id, resultNotes: notes })).unwrap();
    },
    [dispatch],
  );

  const handleCancel = useCallback(
    async (o: MaintenanceOrder) => {
      if (!confirm('¿Cancelar esta orden?')) return;
      await dispatch(cancelOrderThunk(o.id)).unwrap();
    },
    [dispatch],
  );

  return (
    <div className="space-y-3">
      {canEdit && (
        <button
          onClick={() => setOpen(!open)}
          className="rounded-md px-3 py-1.5 text-sm bg-primary text-primary-foreground hover:bg-primary/90"
        >
          {open ? 'Cancelar' : '+ Nueva orden'}
        </button>
      )}
      {open && (
        <form onSubmit={submit} className="border rounded-md p-3 grid grid-cols-1 md:grid-cols-3 gap-3 bg-muted/30">
          <FieldInline label="Tipo">
            <select className="border rounded-md px-2 py-1.5 w-full" {...register('type')}>
              {Object.entries(ORDER_TYPE_LABEL).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </FieldInline>
          <FieldInline label="Fecha programada" error={errors.scheduledAt?.message}>
            <input type="datetime-local" className="border rounded-md px-2 py-1.5 w-full" {...register('scheduledAt')} />
          </FieldInline>
          <FieldInline label="Descripción">
            <input className="border rounded-md px-2 py-1.5 w-full" {...register('description')} />
          </FieldInline>
          <div className="md:col-span-3 flex justify-end">
            <button
              type="submit"
              className="rounded-md px-3 py-1.5 text-sm bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Crear orden
            </button>
          </div>
        </form>
      )}
      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <Th>Tipo</Th>
              <Th>Programada</Th>
              <Th>Ejecutada</Th>
              <Th>Estado</Th>
              <Th>Descripción</Th>
              <Th>Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-muted-foreground">
                  Sin órdenes
                </td>
              </tr>
            )}
            {orders.map((o) => (
              <tr
                key={o.id}
                className={`border-t cursor-pointer ${selectedOrderId === o.id ? 'bg-muted/50' : ''}`}
                onClick={() => setSelectedOrderId(o.id)}
              >
                <td className="px-3 py-2">{ORDER_TYPE_LABEL[o.type]}</td>
                <td className="px-3 py-2">
                  <DateCell value={o.scheduledAt} />
                </td>
                <td className="px-3 py-2">{o.executedAt ? <DateCell value={o.executedAt} /> : '—'}</td>
                <td className="px-3 py-2">
                  <span className={`inline-block rounded px-2 py-0.5 text-xs ${ORDER_STATUS_CLASS[o.status]}`}>
                    {o.status.replace(/_/g, ' ')}
                  </span>
                </td>
                <td className="px-3 py-2">{o.description ?? '—'}</td>
                <td className="px-3 py-2">
                  {canEdit && (o.status === 'programada' || o.status === 'en_curso') && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleComplete(o)}
                        className="text-xs rounded bg-secondary px-2 py-1 hover:bg-secondary/80"
                      >
                        Completar
                      </button>
                      <button
                        onClick={() => handleCancel(o)}
                        className="text-xs rounded bg-destructive text-destructive-foreground px-2 py-1 hover:bg-destructive/90"
                      >
                        Cancelar
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selectedOrderId && (
        <div className="border rounded-md p-3 bg-card">
          <AttachmentsList
            resourceType="maintenance_order"
            resourceId={selectedOrderId}
            readOnly={!canEdit}
            title="Adjuntos de la orden seleccionada"
            accept=".pdf,.doc,.docx,.zip,image/*"
          />
        </div>
      )}
    </div>
  );
}

const recordSchema = z.object({
  orderId: z.string().min(1, 'Seleccioná una orden'),
  performedAt: z.string().min(1, 'Requerido'),
  summary: z.string().min(1, 'Requerido').max(2000),
  hoursSpent: z.coerce.number().nonnegative().optional(),
});
type RecordForm = z.infer<typeof recordSchema>;

function RecordsTab({
  orders,
  records,
  canEdit,
}: {
  orders: MaintenanceOrder[];
  records: { id: string; performedAt: string; summary: string; hoursSpent?: number | null; order?: { id: string; type: string } | null }[];
  canEdit: boolean;
}) {
  const dispatch = useDispatch<AppDispatch>();
  const [open, setOpen] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RecordForm>({
    resolver: zodResolver(recordSchema),
    defaultValues: { orderId: '', performedAt: '', summary: '' },
  });

  const submit = handleSubmit(async (values) => {
    const payload: MaintenanceRecordPayload = {
      orderId: values.orderId,
      performedAt: new Date(values.performedAt).toISOString(),
      summary: values.summary,
      hoursSpent: values.hoursSpent ?? null,
    };
    await dispatch(createRecordThunk(payload)).unwrap();
    reset({ orderId: '', performedAt: '', summary: '' });
    setOpen(false);
  });

  return (
    <div className="space-y-3">
      {canEdit && orders.length > 0 && (
        <button
          onClick={() => setOpen(!open)}
          className="rounded-md px-3 py-1.5 text-sm bg-primary text-primary-foreground hover:bg-primary/90"
        >
          {open ? 'Cancelar' : '+ Nuevo registro'}
        </button>
      )}
      {open && (
        <form onSubmit={submit} className="border rounded-md p-3 grid grid-cols-1 md:grid-cols-2 gap-3 bg-muted/30">
          <FieldInline label="Orden vinculada" error={errors.orderId?.message}>
            <select className="border rounded-md px-2 py-1.5 w-full" {...register('orderId')}>
              <option value="">Seleccionar…</option>
              {orders.map((o) => (
                <option key={o.id} value={o.id}>
                  {ORDER_TYPE_LABEL[o.type]} — {new Date(o.scheduledAt).toLocaleDateString()}
                </option>
              ))}
            </select>
          </FieldInline>
          <FieldInline label="Fecha de ejecución" error={errors.performedAt?.message}>
            <input
              type="datetime-local"
              className="border rounded-md px-2 py-1.5 w-full"
              {...register('performedAt')}
            />
          </FieldInline>
          <FieldInline label="Resumen" error={errors.summary?.message} colSpan={2}>
            <textarea rows={2} className="border rounded-md px-2 py-1.5 w-full" {...register('summary')} />
          </FieldInline>
          <FieldInline label="Horas insumidas">
            <input
              type="number"
              step="0.1"
              min={0}
              className="border rounded-md px-2 py-1.5 w-full"
              {...register('hoursSpent')}
            />
          </FieldInline>
          <div className="md:col-span-2 flex justify-end">
            <button
              type="submit"
              className="rounded-md px-3 py-1.5 text-sm bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Crear registro
            </button>
          </div>
        </form>
      )}
      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <Th>Ejecutado</Th>
              <Th>Orden</Th>
              <Th>Resumen</Th>
              <Th>Horas</Th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-center text-muted-foreground">
                  Sin registros
                </td>
              </tr>
            )}
            {records.map((r) => (
              <tr
                key={r.id}
                className={`border-t cursor-pointer ${selectedRecordId === r.id ? 'bg-muted/50' : ''}`}
                onClick={() => setSelectedRecordId(r.id)}
              >
                <td className="px-3 py-2">
                  <DateCell value={r.performedAt} />
                </td>
                <td className="px-3 py-2">{r.order?.type ?? '—'}</td>
                <td className="px-3 py-2">{r.summary}</td>
                <td className="px-3 py-2">{r.hoursSpent ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selectedRecordId && (
        <div className="border rounded-md p-3 bg-card">
          <AttachmentsList
            resourceType="maintenance_record"
            resourceId={selectedRecordId}
            readOnly={!canEdit}
            title="Adjuntos del registro seleccionado"
            accept=".pdf,.doc,.docx,.zip,image/*"
          />
        </div>
      )}
    </div>
  );
}

const calibrationSchema = z.object({
  calibratedAt: z.string().min(1, 'Requerido'),
  result: z.enum(['conforme', 'no_conforme']),
  nextCalibrationAt: z.string().optional(),
  notes: z.string().trim().max(5000).optional(),
});
type CalibrationForm = z.infer<typeof calibrationSchema>;

function CalibrationsTab({
  equipmentId,
  records,
  canEdit,
}: {
  equipmentId: string;
  records: CalibrationRecord[];
  canEdit: boolean;
}) {
  const dispatch = useDispatch<AppDispatch>();
  const [open, setOpen] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CalibrationForm>({
    resolver: zodResolver(calibrationSchema),
    defaultValues: { calibratedAt: '', result: 'conforme', nextCalibrationAt: '', notes: '' },
  });

  const result = watch('result');

  const submit = handleSubmit(async (values) => {
    const payload: CalibrationRecordPayload = {
      calibratedAt: new Date(values.calibratedAt).toISOString(),
      result: values.result,
      nextCalibrationAt: values.nextCalibrationAt
        ? new Date(values.nextCalibrationAt).toISOString()
        : null,
      notes: values.notes?.trim() || null,
    };
    await dispatch(createCalibrationThunk({ equipmentId, payload })).unwrap();
    await dispatch(loadOneEquipment(equipmentId));
    reset({ calibratedAt: '', result: 'conforme', nextCalibrationAt: '', notes: '' });
    setOpen(false);
  });

  return (
    <div className="space-y-4">
      {canEdit && (
        <button
          onClick={() => setOpen((v) => !v)}
          className="rounded-md px-3 py-1.5 text-sm bg-primary text-primary-foreground hover:bg-primary/90"
        >
          {open ? 'Cancelar' : '+ Registrar calibración'}
        </button>
      )}
      {open && canEdit && (
        <form onSubmit={submit} className="border rounded-md p-4 bg-card grid grid-cols-1 md:grid-cols-2 gap-3">
          <FieldInline label="Fecha de calibración" error={errors.calibratedAt?.message}>
            <input type="date" className="border rounded-md px-2 py-1.5 w-full" {...register('calibratedAt')} />
          </FieldInline>
          <FieldInline label="Resultado" error={errors.result?.message}>
            <select className="border rounded-md px-2 py-1.5 w-full" {...register('result')}>
              <option value="conforme">Conforme</option>
              <option value="no_conforme">No conforme</option>
            </select>
          </FieldInline>
          <FieldInline label="Próxima calibración (opcional)">
            <input type="date" className="border rounded-md px-2 py-1.5 w-full" {...register('nextCalibrationAt')} />
          </FieldInline>
          <FieldInline label="Notas" colSpan={2}>
            <textarea rows={2} className="border rounded-md px-2 py-1.5 w-full" {...register('notes')} />
          </FieldInline>
          {result === 'no_conforme' && (
            <p className="md:col-span-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              Una calibración no conforme generará automáticamente una no conformidad en CAPA/QMS.
            </p>
          )}
          <div className="md:col-span-2 flex justify-end">
            <button
              type="submit"
              className="rounded-md px-3 py-1.5 text-sm bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Guardar calibración
            </button>
          </div>
        </form>
      )}
      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr>
              <Th>Fecha</Th>
              <Th>Resultado</Th>
              <Th>Próxima</Th>
              <Th>Ejecutado por</Th>
              <Th>Notas</Th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-muted-foreground">
                  Sin calibraciones registradas
                </td>
              </tr>
            )}
            {records.map((r) => (
              <tr
                key={r.id}
                className={`border-t cursor-pointer ${selectedRecordId === r.id ? 'bg-muted/50' : ''}`}
                onClick={() => setSelectedRecordId(r.id)}
              >
                <td className="px-3 py-2">
                  <DateCell value={r.calibratedAt} />
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-block rounded px-2 py-0.5 text-xs ${
                      r.result === 'conforme' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {CALIBRATION_RESULT_LABEL[r.result]}
                  </span>
                  {r.bridgeRecordId && (
                    <Link
                      to={`/registros/no-conformidades/${r.bridgeRecordId}`}
                      className="ml-1 text-[10px] text-primary hover:underline"
                    >
                      Ver NC
                    </Link>
                  )}
                </td>
                <td className="px-3 py-2">
                  {r.nextCalibrationAt ? <DateCell value={r.nextCalibrationAt} /> : '—'}
                </td>
                <td className="px-3 py-2">{r.performedBy?.email ?? '—'}</td>
                <td className="px-3 py-2">{r.notes ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {selectedRecordId && (
        <div className="border rounded-md p-3 bg-card">
          <AttachmentsList
            resourceType="calibration_record"
            resourceId={selectedRecordId}
            readOnly={!canEdit}
            title="Certificado de calibración"
            accept=".pdf,image/*"
          />
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-left px-3 py-2 font-medium">{children}</th>;
}

function FieldInline({
  label,
  children,
  error,
  colSpan,
}: {
  label: string;
  children: React.ReactNode;
  error?: string;
  colSpan?: 1 | 2;
}) {
  return (
    <div className={colSpan === 2 ? 'md:col-span-2' : ''}>
      <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}
